import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs';
import { TranslocoPipe } from '@jsverse/transloco';
import { Shipment } from '../models/shipment';
import { ShipmentScheduleCorrectionRequest, ShipmentService } from '../shipment.service';

@Component({
    selector: 'app-shipment-schedule-correction',
    standalone: true,
    templateUrl: './shipment-schedule-correction.component.html',
    styleUrls: ['../../shared/party-quick-create/party-quick-create-dialog.component.css',
        './shipment-schedule-correction.component.css'],
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, DialogModule,
        IftaLabelModule, InputGroupModule, InputGroupAddonModule, TextareaModule, TranslocoPipe]
})
export class ShipmentScheduleCorrectionComponent {
    @Input() shipment?: Shipment;
    @Output() corrected = new EventEmitter<void>();

    visible = false;
    saving = false;
    pickup?: Date;
    dropoff?: Date;
    reason = '';
    error = '';
    expectedPickupTime: number | null = null;
    expectedDropoffTime: number | null = null;

    constructor(private shipmentService: ShipmentService) {}

    open(): void {
        if (!this.shipment?.shipmentId) return;
        const pickup = this.asDate(this.shipment.pickupTime ?? this.shipment.requestedBookingTime);
        const dropoff = this.asDate(this.shipment.dropoffTime);
        this.pickup = pickup;
        this.dropoff = dropoff;
        this.expectedPickupTime = pickup?.getTime() ?? null;
        this.expectedDropoffTime = dropoff?.getTime() ?? null;
        this.reason = '';
        this.error = '';
        this.visible = true;
    }

    get canSave(): boolean {
        const pickup = this.pickup?.getTime();
        const dropoff = this.dropoff?.getTime();
        return !this.saving && !!pickup && !!dropoff && dropoff > pickup
            && !!this.reason.trim() && this.reason.trim().length <= 1000
            && (pickup !== this.expectedPickupTime || dropoff !== this.expectedDropoffTime);
    }

    save(): void {
        if (!this.canSave || !this.shipment?.shipmentId || !this.pickup || !this.dropoff) return;
        const request: ShipmentScheduleCorrectionRequest = {
            pickupTime: this.pickup.getTime(),
            dropoffTime: this.dropoff.getTime(),
            expectedPickupTime: this.expectedPickupTime,
            expectedDropoffTime: this.expectedDropoffTime,
            reason: this.reason.trim()
        };
        this.saving = true;
        this.error = '';
        this.shipmentService.correctSchedule(this.shipment.shipmentId, request)
            .pipe(finalize(() => this.saving = false)).subscribe({
                next: () => {
                    this.visible = false;
                    this.corrected.emit();
                },
                error: error => this.error = error?.error?.detail || error?.error?.message
                    || error?.message || 'The shipment dates could not be changed.'
            });
    }

    private asDate(value?: Date | number | string | null): Date | undefined {
        if (value == null) return undefined;
        const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
}
